import { skipToken, useQuery } from '@tanstack/react-query';
import { getV1MessagingChannelsId } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { MessagingChannel } from '~hooks/useMessaging/models';
import useReportRequestError from '~hooks/useReportRequestError';
import { messagingChannelQueryKey } from '~queries/messagingQueryKeys';
import { Nullable } from '~types/Nullable';

export const fetchMessagingChannel = async (channelId: string, signal?: AbortSignal) => {
  const response = await getV1MessagingChannelsId({ path: { id: channelId }, signal });

  return MessagingChannel.parse(response.data.data);
};

export default function useMessagingChannelQuery({
  channelId,
  companyId,
}: {
  channelId: Nullable<string>;
  companyId: Nullable<string>;
}) {
  const query = useQuery({
    queryKey: messagingChannelQueryKey(companyId, channelId),
    queryFn:
      companyId && channelId
        ? ({ signal }) => fetchMessagingChannel(channelId, signal)
        : skipToken,
    retry: false,
  });

  // A failed first load has the header's inline Retry; a failed refetch keeps the loaded conversation on screen.
  useReportRequestError({
    error: query.error,
    errorUpdatedAt: query.errorUpdatedAt,
    message: t('error_messages.messaging.failed_to_fetch_channel'),
    shouldReport: query.isRefetchError,
  });

  return {
    channel: query.data ?? null,
    hasLoadFailed: query.isLoadingError,
    isLoading: query.isLoading,
    isRefreshing: query.isRefetching,
    retryLoad: query.refetch,
  };
}
